import React, { useEffect, useState } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { api } from '../lib/api';
import {
  Loader2, CheckCircle2, Phone, User as UserIcon, Hash, Award, Upload, Trophy
} from 'lucide-react';

const PublicResultForm = () => {
  const { slug } = useParams();
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState('');

  // Built-in fields
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [rollNumber, setRollNumber] = useState('');
  const [rank, setRank] = useState('');
  const [remarks, setRemarks] = useState('');
  const [doc, setDoc] = useState(null);
  const [customValues, setCustomValues] = useState({});
  const [customFiles, setCustomFiles] = useState({});

  useEffect(() => {
    api.getPublicResultForm(slug)
      .then((res) => setForm(res.data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  const setCustom = (key, value) => setCustomValues(prev => ({ ...prev, [key]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (name.trim().length < 2) { setError('Please enter your full name'); return; }
    const cleanPhone = phone.replace(/[^0-9+]/g, '');
    if (cleanPhone.length < 10) { setError('Please enter a valid phone number'); return; }

    // Check required custom fields
    for (const f of (form.custom_fields || [])) {
      if (!f.required) continue;
      if (f.type === 'file') {
        if (!customFiles[f.key]) { setError(`${f.label} is required`); return; }
      } else {
        if (!(customValues[f.key] || '').toString().trim()) { setError(`${f.label} is required`); return; }
      }
    }

    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('name', name.trim());
      fd.append('contact_number', cleanPhone);
      if (rank) fd.append('rank', rank);
      if (rollNumber) fd.append('roll_number', rollNumber);
      if (remarks) fd.append('remarks', remarks);
      if (doc) fd.append('document', doc);
      const customKeyed = {};
      (form.custom_fields || []).forEach(f => {
        if (f.type === 'file') return; // files handled separately
        if (customValues[f.key] !== undefined && customValues[f.key] !== '') {
          customKeyed[f.label] = customValues[f.key];
        }
      });
      if (Object.keys(customKeyed).length > 0) {
        fd.append('custom_fields_json', JSON.stringify(customKeyed));
      }
      // Per-field file uploads
      (form.custom_fields || []).forEach(f => {
        if (f.type === 'file' && customFiles[f.key]) {
          fd.append(`custom_file__${f.key}__${f.label}`, customFiles[f.key]);
        }
      });
      const res = await api.submitPublicResultForm(slug, fd);
      setSuccess(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-slate-50"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div>;
  }
  if (notFound) return <Navigate to="/login" replace />;

  const color = form.primary_color || '#7c3aed';

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4 py-8"
      style={{ background: `linear-gradient(135deg, ${color} 0%, ${color}dd 50%, #0f172a 100%)` }}
    >
      <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden" data-testid="public-result-form">
        {form.poster_url ? (
          <div className="relative">
            <img src={form.poster_url} alt={form.exam_name} className="w-full h-48 sm:h-56 object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
            <div className="absolute bottom-3 left-4 right-4 text-white">
              <div className="text-xs uppercase tracking-wider opacity-90">{form.exam_name}</div>
              <div className="text-xl font-bold leading-tight drop-shadow">{form.title}</div>
            </div>
          </div>
        ) : (
          <div className="py-10 px-6 text-white text-center" style={{ backgroundColor: color }}>
            <div className="inline-flex items-center justify-center w-14 h-14 bg-white/15 rounded-full mb-3 backdrop-blur"><Trophy size={28} /></div>
            <div className="text-xs uppercase tracking-wider opacity-90 mb-1">{form.exam_name}</div>
            <h1 className="text-2xl sm:text-3xl font-bold leading-tight">{form.title}</h1>
          </div>
        )}

        <div className="p-6 sm:p-8">
          {form.subtitle && <p className="text-slate-600 text-sm mb-6 leading-relaxed text-center">{form.subtitle}</p>}

          {success ? (
            <div className="text-center py-4" data-testid="result-form-success">
              <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center" style={{ backgroundColor: `${color}22` }}>
                <CheckCircle2 size={36} style={{ color }} />
              </div>
              <h2 className="text-xl font-bold text-slate-900 mb-2">Submission Received!</h2>
              <p className="text-slate-600 text-sm mb-2">{success.message || form.success_message || 'Thank you!'}</p>
              {success.is_update && (
                <p className="text-xs text-slate-500 italic mb-4">Your earlier submission was updated.</p>
              )}
              <button
                onClick={() => { setSuccess(null); setName(''); setPhone(''); setRollNumber(''); setRank(''); setRemarks(''); setDoc(null); setCustomValues({}); setCustomFiles({}); }}
                className="mt-2 w-full py-3 rounded-lg border-2 border-slate-200 text-slate-700 font-medium hover:bg-slate-50"
              >
                Submit Another
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1.5">Your Name *</label>
                <div className="relative">
                  <UserIcon size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Full name" data-testid="result-name-input"
                    className="w-full pl-10 pr-3 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2"
                    style={{ '--tw-ring-color': color }} required autoComplete="name" />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1.5">Phone Number *</label>
                <div className="relative">
                  <Phone size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="10-digit mobile" data-testid="result-phone-input"
                    className="w-full pl-10 pr-3 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2"
                    style={{ '--tw-ring-color': color }} required autoComplete="tel" inputMode="tel" />
                </div>
              </div>

              {form.show_roll_number && (
                <div>
                  <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1.5">Roll Number</label>
                  <div className="relative">
                    <Hash size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input type="text" value={rollNumber} onChange={(e) => setRollNumber(e.target.value)} placeholder="Optional"
                      className="w-full pl-10 pr-3 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2"
                      style={{ '--tw-ring-color': color }} />
                  </div>
                </div>
              )}

              {form.show_rank && (
                <div>
                  <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1.5">Rank / Score</label>
                  <div className="relative">
                    <Award size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input type="text" value={rank} onChange={(e) => setRank(e.target.value)} placeholder="Optional"
                      className="w-full pl-10 pr-3 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2"
                      style={{ '--tw-ring-color': color }} />
                  </div>
                </div>
              )}

              {/* Custom fields */}
              {(form.custom_fields || []).filter(f => f && f.label && f.label.trim()).map((f) => (
                <div key={f.key}>
                  <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1.5">
                    {f.label} {f.required && '*'}
                  </label>
                  {f.type === 'textarea' ? (
                    <textarea rows={3}
                      value={customValues[f.key] || ''}
                      onChange={(e) => setCustom(f.key, e.target.value)}
                      required={f.required}
                      className="w-full px-3 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 resize-none"
                      style={{ '--tw-ring-color': color }}
                    />
                  ) : f.type === 'select' ? (
                    <select
                      value={customValues[f.key] || ''}
                      onChange={(e) => setCustom(f.key, e.target.value)}
                      required={f.required}
                      className="w-full px-3 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 bg-white"
                      style={{ '--tw-ring-color': color }}
                    >
                      <option value="">Select</option>
                      {(f.options || []).map(opt => <option key={opt} value={opt}>{opt}</option>)}
                    </select>
                  ) : f.type === 'boolean' ? (
                    <div className="flex gap-3">
                      {['Yes', 'No'].map(opt => (
                        <button
                          type="button"
                          key={opt}
                          onClick={() => setCustom(f.key, opt)}
                          className={`flex-1 py-3 rounded-lg border-2 font-semibold transition ${
                            customValues[f.key] === opt
                              ? 'text-white shadow-md'
                              : 'border-slate-300 text-slate-600 hover:border-slate-400 bg-white'
                          }`}
                          style={customValues[f.key] === opt ? { backgroundColor: color, borderColor: color } : {}}
                        >
                          {opt}
                        </button>
                      ))}
                    </div>
                  ) : f.type === 'file' ? (
                    <label className="flex items-center gap-2 px-4 py-3 border-2 border-dashed border-slate-300 rounded-lg hover:border-purple-400 cursor-pointer transition">
                      <Upload size={16} className="text-slate-500" />
                      <span className="text-sm text-slate-600 truncate">
                        {customFiles[f.key] ? customFiles[f.key].name : 'Click to upload (max 10MB)'}
                      </span>
                      <input
                        type="file"
                        accept="image/*,application/pdf"
                        className="hidden"
                        required={f.required && !customFiles[f.key]}
                        onChange={(e) => {
                          const file = e.target.files[0];
                          if (file && file.size > 10 * 1024 * 1024) { setError(`${f.label}: file too large (max 10MB)`); return; }
                          setCustomFiles(prev => ({ ...prev, [f.key]: file }));
                        }}
                      />
                    </label>
                  ) : (
                    <input
                      type={f.type}
                      value={customValues[f.key] || ''}
                      onChange={(e) => setCustom(f.key, e.target.value)}
                      required={f.required}
                      className="w-full px-3 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2"
                      style={{ '--tw-ring-color': color }}
                    />
                  )}
                </div>
              ))}

              {form.allow_document && (
                <div>
                  <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1.5">Scorecard / Proof (optional)</label>
                  <label className="flex items-center gap-2 px-4 py-3 border-2 border-dashed border-slate-300 rounded-lg hover:border-purple-400 cursor-pointer transition">
                    <Upload size={16} className="text-slate-500" />
                    <span className="text-sm text-slate-600 truncate">
                      {doc ? doc.name : 'Click to upload (max 10MB)'}
                    </span>
                    <input type="file" accept="image/*,application/pdf" className="hidden" onChange={(e) => setDoc(e.target.files[0])} />
                  </label>
                </div>
              )}

              <div>
                <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1.5">Any Remarks (optional)</label>
                <textarea value={remarks} onChange={(e) => setRemarks(e.target.value)} rows={2}
                  className="w-full px-3 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 resize-none"
                  style={{ '--tw-ring-color': color }} />
              </div>

              {error && <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</div>}

              <button
                type="submit"
                disabled={submitting}
                data-testid="result-form-submit-btn"
                className="w-full py-3.5 rounded-lg text-white font-semibold text-base shadow-md hover:opacity-95 disabled:opacity-60 flex items-center justify-center gap-2"
                style={{ backgroundColor: color }}
              >
                {submitting ? <><Loader2 className="animate-spin" size={18} />Submitting…</> : (form.cta_text || 'Submit My Result')}
              </button>

              <p className="text-xs text-slate-400 text-center mt-2">{form.footer_text}</p>
            </form>
          )}
        </div>

        <div className="px-6 py-3 bg-slate-50 border-t border-slate-100 text-center text-xs text-slate-400">
          Exampur · Trusted by lakhs of aspirants
        </div>
      </div>
    </div>
  );
};

export default PublicResultForm;
